/**
 * Reactive counterparts to `core/permissions/permission-engine.ts`'s
 * plain functions — these re-render the calling component when the
 * underlying permission/role set changes (a real Zustand selector
 * subscription), whereas the plain functions read a one-off snapshot
 * (correct for imperative checks outside render, e.g. inside an event
 * handler or a non-component service call).
 */
import { useAuthStore } from '@store/auth.store';
import { isRecordCorrectionOperator } from '@core/permissions/record-correction-operator';
import { FinancialPermissions } from '@nexao/contracts';

export function usePermission(permission: string): boolean {
  return useAuthStore((state) => state.permissions.includes(permission));
}

export function useAllPermissions(permissions: string[]): boolean {
  return useAuthStore((state) => permissions.every((permission) => state.permissions.includes(permission)));
}

export function useAnyPermission(permissions: string[]): boolean {
  return useAuthStore((state) => permissions.some((permission) => state.permissions.includes(permission)));
}

export function useRole(role: string): boolean {
  return useAuthStore((state) => state.roles.includes(role));
}

/** True only for the one account allowed to correct records after the fact — see `record-correction-operator.ts`. */
export function useIsRecordCorrectionOperator(): boolean {
  return useAuthStore((state) => isRecordCorrectionOperator(state.user?.email));
}

/**
 * MAY THIS USER ENTER A CORRECTION REQUEST? (desk, 2026-10-05: "1. Request entry by frontdesk users.")
 *
 * The three-stage workflow starts with the FRONT DESK entering the request, so the button is no longer the correction account's
 * alone: it is offered to anyone holding the request permission — the same permission the server's request endpoints already
 * demand — and still to the correction account, which is always allowed. Entering a request changes nothing on its own; a
 * selected verifier and a selected approver, both different people, decide what happens (see `correction-workflow.service.ts`).
 *
 * This only decides what the SCREEN offers; the server gates the request routes on the permission and refuses a request when no
 * verifier and approver have been selected.
 */
export function useCanRequestCorrection(): boolean {
  return useAuthStore(
    (state) =>
      state.permissions.includes(FinancialPermissions.FINANCIAL_PAYMENT_CORRECTION_REQUEST) || isRecordCorrectionOperator(state.user?.email),
  );
}

/**
 * MAY THIS USER LOOK AT EVERY CORRECTION REQUEST? (owner, 2026-10-18: "for admin should see all modification requests and their status".)
 *
 * The administrator who already chooses who verifies and approves - the holder of the approval permission, the SuperAdmin - gets the read-only register
 * under the header's Corrections page. Looking is all it allows: verifying and approving still belong to the selected people, and the server gates the
 * list on this same permission (`GET /financial/correction-workflow/overview`).
 */
export function useCanOverseeCorrections(): boolean {
  return useAuthStore((state) => state.permissions.includes(FinancialPermissions.FINANCIAL_PAYMENT_CORRECTION_APPROVE));
}
